import React, { useRef, useState } from 'react';
import { Upload, Download, AlertTriangle, CheckCircle, ArrowRight, Info } from 'lucide-react';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { parseExcelFile, autoMatch, type ParseResult } from '@/lib/excel';
import type {
  GenericPreviewResponse,
  GenericPreviewRow,
  GenericImportCommitResponse,
} from '@/lib/api';

// ── Config types ─────────────────────────────────────────────────────────────

export interface ImportFieldDef {
  key:        string;
  label:      string;
  required?:  boolean;
  description?: string;
}

export interface PreviewColumn<R> {
  header: string;
  render: (row: GenericPreviewRow<R>) => React.ReactNode;
}

interface Props<R> {
  buttonLabel:        string;
  dialogTitle:        string;
  /** Singular noun for the completion message, e.g. "piece of equipment" / "part". */
  noun:               string;
  nounPlural:         string;
  fields:             ImportFieldDef[];
  onDownloadTemplate: () => void;
  previewColumns:     PreviewColumn<R>[];
  importPreview:      (rows: Array<Record<string, unknown>>) => Promise<GenericPreviewResponse<R>>;
  importCommit:       (rows: Array<Record<string, unknown>>) => Promise<GenericImportCommitResponse>;
  onComplete?:        () => void;
}

const SKIP = '';

type Step = 'upload' | 'map' | 'validate' | 'done';

// ── Component ────────────────────────────────────────────────────────────────

export function PreviewImportDialog<R>({
  buttonLabel,
  dialogTitle,
  noun,
  nounPlural,
  fields,
  onDownloadTemplate,
  previewColumns,
  importPreview,
  importCommit,
  onComplete,
}: Props<R>) {
  const [open, setOpen]           = useState(false);
  const [step, setStep]           = useState<Step>('upload');
  const [parsed, setParsed]       = useState<ParseResult | null>(null);
  const [mapping, setMapping]     = useState<Record<string, string>>({});
  const [preview, setPreview]     = useState<GenericPreviewResponse<R> | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<GenericImportCommitResponse | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep('upload'); setParsed(null); setMapping({});
    setPreview(null); setCommitResult(null);
    setPreviewing(false); setCommitting(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    const wasDone = step === 'done';
    setOpen(false);
    reset();
    if (wasDone && onComplete) onComplete();
  };

  // ── Step 1: upload ──────────────────────────────────────────────────────────

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await parseExcelFile(file);
      setParsed(result);
      if (result.errors.length === 0 && result.rows.length > 0) {
        const auto = autoMatch(fields.map(f => f.key), result.headers);
        setMapping(auto);
        setStep('map');
      }
    } catch (err) {
      setParsed({ headers: [], rows: [], errors: [(err as Error).message] });
    }
  };

  // ── Step 2: map ───────────────────────────────────────────────────────────

  const requiredsMapped = fields.filter(f => f.required).every(f => mapping[f.key] && mapping[f.key] !== SKIP);

  const setFieldMapping = (fieldKey: string, fileCol: string) =>
    setMapping(m => ({ ...m, [fieldKey]: fileCol }));

  const handleValidate = async () => {
    if (!parsed) return;
    setPreviewing(true);
    try {
      const rows = parsed.rows.map((raw, idx) => {
        const row: Record<string, unknown> = { row_index: idx };
        for (const f of fields) {
          const col = mapping[f.key];
          if (col && col !== SKIP) {
            const val = (raw[col] ?? '').trim();
            if (val) row[f.key] = val;
          }
        }
        return row;
      });
      const res = await importPreview(rows);
      setPreview(res);
      setStep('validate');
    } catch (err) {
      alert(`Preview failed: ${(err as Error).message}`);
    } finally {
      setPreviewing(false);
    }
  };

  // ── Step 3: validate → commit ─────────────────────────────────────────────

  const handleCommit = async () => {
    if (!preview) return;
    // Only ready + warning rows are importable. Re-attach row_index onto the
    // resolved payload; the backend strips any extra display-only fields.
    const importable = preview.rows
      .filter(r => (r.status === 'ready' || r.status === 'warning') && r.resolved)
      .map(r => ({ ...(r.resolved as Record<string, unknown>), row_index: r.row_index }));

    if (importable.length === 0) return;

    setCommitting(true);
    try {
      const res = await importCommit(importable);
      setCommitResult(res);
      setStep('done');
    } catch (err) {
      alert(`Import failed: ${(err as Error).message}`);
    } finally {
      setCommitting(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const stepLabel = (s: Step, n: number, title: string) => (
    <div className="flex items-center gap-2 mb-3">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
        ${step === s ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-500'}`}>{n}</span>
      <span className={`text-sm font-medium ${step === s ? 'text-slate-900' : 'text-slate-400'}`}>{title}</span>
    </div>
  );

  const statusPill = (r: GenericPreviewRow<R>) => {
    if (r.status === 'ready')     return <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle size={12} /> Ready</span>;
    if (r.status === 'warning')   return <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle size={12} /> Warning</span>;
    if (r.status === 'duplicate') return <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Info size={12} /> Duplicate</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-red-700"><AlertTriangle size={12} /> Error</span>;
  };

  const importableCount = preview ? preview.summary.ready + preview.summary.warnings : 0;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Upload size={14} /> {buttonLabel}
      </Button>

      <Dialog open={open} onClose={handleClose} title={dialogTitle} className="max-w-4xl">
        <div className="flex flex-col gap-5">

          {/* ── Step 1: Upload ────────────────────────────────────────────── */}
          <div>
            {stepLabel('upload', 1, 'Upload your file')}
            {step === 'upload' && (
              <div className="flex flex-col gap-3">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-500">
                    Don't have a file yet?{' '}
                    <button onClick={onDownloadTemplate} className="text-brand-600 underline underline-offset-2">
                      Download our template
                    </button>{' '}— or upload your own and map the columns in the next step.
                  </p>
                  <Button variant="secondary" size="sm" onClick={onDownloadTemplate}>
                    <Download size={13} /> Template
                  </Button>
                </div>
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-4 py-8 cursor-pointer hover:border-brand-400 hover:bg-brand-50/30 transition-colors">
                  <Upload size={24} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">Click to choose .xlsx or .csv</span>
                  <span className="text-xs text-slate-400">Any column names — you'll map them in the next step</span>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleFile}
                  />
                </label>
                {parsed?.errors.map((e, i) => (
                  <p key={i} className="text-sm text-red-600 flex items-center gap-1.5">
                    <AlertTriangle size={14} /> {e}
                  </p>
                ))}
              </div>
            )}
            {step !== 'upload' && parsed && (
              <p className="text-xs text-green-700 bg-green-50 rounded px-3 py-1.5 inline-flex items-center gap-1.5">
                <CheckCircle size={13} /> {parsed.rows.length} rows loaded from file
                <button onClick={reset} className="ml-2 text-slate-400 hover:text-slate-600 underline underline-offset-2">
                  Change file
                </button>
              </p>
            )}
          </div>

          {/* ── Step 2: Map ──────────────────────────────────────────────── */}
          {(step === 'map' || step === 'validate' || step === 'done') && (
            <div>
              {stepLabel('map', 2, 'Map your columns to VantixMT fields')}
              {step === 'map' && (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-slate-500">
                    We auto-detected matches where possible. Fix any that are wrong, or set them to{' '}
                    <span className="font-medium">— skip —</span>.
                  </p>
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 w-1/3">VantixMT field</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 w-8"></th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Your file's column</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {fields.map(f => {
                          const mapped = mapping[f.key] ?? '';
                          const isMatched = mapped !== SKIP && mapped !== '';
                          return (
                            <tr key={f.key} className={f.required && !isMatched ? 'bg-red-50' : ''}>
                              <td className="px-4 py-2">
                                <span className="font-medium text-slate-800">{f.label}</span>
                                {f.required && <span className="text-red-500 ml-0.5 text-xs">*</span>}
                                {f.description && (
                                  <p className="text-xs text-slate-400 mt-0.5">{f.description}</p>
                                )}
                              </td>
                              <td className="px-2 py-2 text-slate-300"><ArrowRight size={14} /></td>
                              <td className="px-4 py-2">
                                <select
                                  value={mapped}
                                  onChange={e => setFieldMapping(f.key, e.target.value)}
                                  className={`w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500
                                    ${isMatched ? 'border-green-300 bg-green-50 text-green-800' : 'border-slate-300 bg-white text-slate-500'}
                                    ${f.required && !isMatched ? 'border-red-300 bg-red-50' : ''}`}
                                >
                                  <option value="">— skip —</option>
                                  {parsed?.headers.map(h => <option key={h} value={h}>{h}</option>)}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {!requiredsMapped && (
                    <p className="text-xs text-red-600 flex items-center gap-1.5">
                      <AlertTriangle size={13} /> Map all required fields (*) before continuing.
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={reset}>Back</Button>
                    <Button size="sm" disabled={!requiredsMapped || previewing} onClick={handleValidate}>
                      {previewing ? 'Validating…' : 'Validate'}
                    </Button>
                  </div>
                </div>
              )}
              {(step === 'validate' || step === 'done') && (
                <div className="rounded-lg border border-slate-200 px-4 py-2 bg-slate-50">
                  <p className="text-xs text-slate-500">
                    {fields.filter(f => mapping[f.key]).length} fields mapped.{' '}
                    <button
                      onClick={() => setStep('map')}
                      className="text-brand-600 underline underline-offset-2"
                      disabled={step === 'done'}
                    >Edit mapping</button>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Validate ─────────────────────────────────────────── */}
          {(step === 'validate' || step === 'done') && preview && (
            <div>
              {stepLabel('validate', 3, step === 'done' ? 'Validation complete' : 'Validate parsed result')}
              <p className="text-sm mb-2">
                <span className="text-green-700 font-medium">{preview.summary.ready} ready</span>
                {preview.summary.warnings > 0 && (
                  <span className="text-amber-700 font-medium ml-3">{preview.summary.warnings} warnings</span>
                )}
                {preview.summary.errors > 0 && (
                  <span className="text-red-700 font-medium ml-3">{preview.summary.errors} errors</span>
                )}
                {preview.summary.duplicates > 0 && (
                  <span className="text-slate-600 font-medium ml-3">{preview.summary.duplicates} duplicates</span>
                )}
              </p>
              <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-80">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 border-b border-slate-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium w-7">#</th>
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Status</th>
                      {previewColumns.map(c => (
                        <th key={c.header} className="px-2 py-1.5 text-left text-slate-500 font-medium whitespace-nowrap">{c.header}</th>
                      ))}
                      <th className="px-2 py-1.5 text-left text-slate-500 font-medium">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.rows.map(r => (
                      <tr key={r.row_index} className={
                        r.status === 'ready' ? 'bg-green-50/40' :
                        r.status === 'warning' ? 'bg-amber-50/40' :
                        r.status === 'duplicate' ? 'bg-slate-50' : 'bg-red-50/40'
                      }>
                        <td className="px-2 py-1 text-slate-400">{r.row_index + 1}</td>
                        <td className="px-2 py-1">{statusPill(r)}</td>
                        {previewColumns.map(c => (
                          <td key={c.header} className="px-2 py-1 text-slate-700 max-w-[12rem] truncate">{c.render(r)}</td>
                        ))}
                        <td className="px-2 py-1 text-slate-600">
                          {r.error && <span className="text-red-700">{r.error}</span>}
                          {r.warnings && r.warnings.map((w, i) => (
                            <div key={i} className="text-amber-700">{w}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {step === 'validate' && (
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="secondary" size="sm" onClick={() => setStep('map')}>Edit mapping</Button>
                  <Button
                    size="sm"
                    disabled={committing || importableCount === 0}
                    onClick={handleCommit}
                  >
                    {committing
                      ? 'Importing…'
                      : `Import ${importableCount} ready row${importableCount !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Done ────────────────────────────────────────────── */}
          {step === 'done' && commitResult && (
            <div>
              {stepLabel('done', 4, 'Import complete')}
              <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3">
                <p className="text-sm">
                  <span className="text-green-700 font-medium">
                    {commitResult.created} {commitResult.created !== 1 ? nounPlural : noun} imported successfully
                  </span>
                  {commitResult.errors.length > 0 && (
                    <span className="text-red-700 font-medium ml-3">{commitResult.errors.length} failed</span>
                  )}
                </p>
                {commitResult.errors.length > 0 && (
                  <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
                    {commitResult.errors.map(e => (
                      <li key={e.row_index}>Row {e.row_index + 1}: {e.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-1 border-t border-slate-100">
            <Button variant="secondary" onClick={handleClose}>
              {step === 'done' ? 'Close' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
